import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Globe, Zap, Shield, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Button from '../components/ui/Button'
import LanguageSelector from '../components/LanguageSelector'

const LandingPage = () => {
  const { t } = useTranslation()
  
  const features = [
    {
      icon: Globe,
      title: t('features.multiLanguage.title'),
      description: t('features.multiLanguage.description')
    },
    {
      icon: Zap,
      title: t('features.lightningFast.title'),
      description: t('features.lightningFast.description')
    },
    {
      icon: Shield,
      title: t('features.secure.title'),
      description: t('features.secure.description')
    }
  ]

  const benefits = [
    t('benefits.csvSupport'),
    t('benefits.batchTranslation'),
    t('benefits.qualityReview'),
    t('benefits.phraseIntegration'),
    t('benefits.realTimeMonitoring'),
    t('benefits.professionalQuality')
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="px-4 py-6">
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-white dark:bg-white rounded-lg p-2 shadow-sm">
              <img 
                src="/logo.png" 
                alt="T8R - AI Translation Platform" 
                className="h-24 w-auto"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSelector />
            <Link to="/login">
              <Button variant="ghost">{t('navigation.login')}</Button>
            </Link>
            <Link to="/register">
              <Button>{t('navigation.getStarted')}</Button>
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="px-4 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            {t('hero.title')}
            <span className="block text-primary-600">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto">
                {t('hero.startTranslating')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/docs">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                {t('hero.viewDocumentation')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {t('features.whyChoose')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {t('features.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                {t('benefits.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                {t('benefits.subtitle')} 
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">{t('benefits.readyToStart')}</h3>
              <p className="mb-6 opacity-90">
                {t('benefits.joinThousands')}
              </p>
              <Link to="/register">
                <Button variant="secondary" size="lg" className="w-full">
                  {t('benefits.createFreeAccount')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white px-4 py-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white dark:bg-white rounded-lg p-2 shadow-sm">
              <img 
                src="/logo.png" 
                alt="T8R - AI Translation Platform" 
                className="h-16 w-auto"
              />
            </div>
          </div>
          <p className="text-gray-400 mb-8">
            {t('footer.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8">
            <Link to="/docs" className="text-gray-400 hover:text-white transition-colors">
              {t('footer.documentation')}
            </Link>
            <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
              {t('footer.login')}
            </Link>
            <Link to="/register" className="text-gray-400 hover:text-white transition-colors">
              {t('footer.signUp')}
            </Link>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-gray-400 text-sm">
            {t('footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage